import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, authFetch } from "../../App";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays } from "date-fns";
import { de } from "date-fns/locale";
import { ChevronLeft, ChevronRight, MapPin, Clock, Users, Loader2, CalendarDays, PartyPopper } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getHoliday } from "../../utils/holidays";

const API = "/api";
const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function MobileEinsatzplanung() {
  const { user } = useAuth();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoize weekEnd to prevent infinite re-renders
  const weekEnd = useMemo(() => endOfWeek(currentWeekStart, { weekStartsOn: 1 }), [currentWeekStart]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const from = format(currentWeekStart, "yyyy-MM-dd");
      const to = format(weekEnd, "yyyy-MM-dd");
      const res = await authFetch(`${API}/assignments?from=${from}&to=${to}`);
      if (res.ok) setEntries(await res.json());
    } catch {}
    setLoading(false);
  }, [currentWeekStart, weekEnd]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 90s
  useEffect(() => {
    const interval = setInterval(loadData, 90000);
    return () => clearInterval(interval);
  }, [loadData]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const todayStr = format(new Date(), "yyyy-MM-dd");

  const getEntriesForDay = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return entries.filter((e) => e.date === dateStr && (e.user_id === user?.id || e.user_name === user?.name));
  };

  const totalPlanned = entries
    .filter((e) => e.user_id === user?.id || e.user_name === user?.name)
    .reduce((s, e) => s + (e.planned_hours || 0), 0);

  return (
    <motion.div
      className="px-4 py-4 min-h-screen bg-[#0f1117] text-white"
      data-testid="m-einsatzplanung"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))} className="w-10 h-10 flex items-center justify-center rounded-full active:bg-white/10 transition-colors" data-testid="m-ep-prev-week">
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <button onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-center">
          <span className="text-sm font-bold text-white">KW {format(currentWeekStart, "w")}</span>
          <span className="text-[10px] text-gray-500 block">{format(currentWeekStart, "dd.MM.")} - {format(weekEnd, "dd.MM.yyyy")}</span>
        </button>
        <button onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))} className="w-10 h-10 flex items-center justify-center rounded-full active:bg-white/10 transition-colors" data-testid="m-ep-next-week">
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Week summary */}
      <div className="flex items-center justify-between px-3 py-2 bg-white/[0.04] rounded-lg mb-3 border border-white/10">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-[11px] font-semibold text-gray-400">Geplant:</span>
        </div>
        <span className="text-[12px] font-bold text-white font-mono">{totalPlanned}h</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {days.map((day, i) => {
            const dayEntries = getEntriesForDay(day);
            const dateStr = format(day, "yyyy-MM-dd");
            const isToday = dateStr === todayStr;
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            const dayHours = dayEntries.reduce((s, e) => s + (e.planned_hours || 0), 0);
            const holiday = getHoliday(dateStr);

            return (
              <motion.div
                key={dateStr}
                className={`rounded-xl overflow-hidden transition-all ${
                  isToday
                    ? "border-2 border-blue-500/30 bg-blue-500/10"
                    : holiday
                      ? "bg-amber-500/[0.08] border border-amber-500/25"
                      : isWeekend
                        ? "bg-white/[0.02] border border-white/5"
                        : "bg-white/[0.04] border border-white/8"
                }`}
                data-testid={`m-ep-day-${dateStr}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.1 + i * 0.05 }}
              >
                {/* Day header */}
                <div className={`flex items-center justify-between px-3 py-2 ${isToday ? "bg-blue-500/10" : ""}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className={`text-[11px] font-bold ${isToday ? "text-blue-400" : holiday ? "text-amber-300" : isWeekend ? "text-gray-600" : "text-gray-300"}`}>
                      {DAY_NAMES[i]}
                    </span>
                    <span className={`text-[11px] ${isToday ? "text-blue-400" : holiday ? "text-amber-300" : "text-gray-500"}`}>{format(day, "dd.MM.")}</span>
                    {holiday && (
                      <span
                        className="text-[10px] font-semibold text-amber-200 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 truncate flex items-center gap-1"
                        title={holiday.name + (holiday.bundeseinheitlich ? " (bundesweit)" : " (regional)")}
                        data-testid={`m-ep-holiday-${dateStr}`}
                      >
                        <PartyPopper className="w-2.5 h-2.5" />
                        <span className="truncate">{holiday.name}</span>
                      </span>
                    )}
                  </div>
                  {dayHours > 0 && (
                    <span className="text-[10px] font-mono font-semibold text-gray-500 flex-shrink-0">{dayHours}h</span>
                  )}
                </div>

                {/* Entries */}
                {dayEntries.length === 0 ? (
                  <div className="px-3 pb-2">
                    <p className="text-[11px] text-gray-600">
                      {holiday ? "Feiertag" : isWeekend ? "Wochenende" : "Kein Einsatz"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-px">
                    {dayEntries.map((e, j) => (
                      <div key={j} className="flex items-center gap-2 px-3 py-2 bg-white/[0.03]">
                        <div className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: e.project_color || "#6B7280" }} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[12px] font-medium text-gray-200 truncate block">{e.project_name || "Projekt"}</span>
                          {e.notes && <span className="text-[10px] text-gray-500 truncate block">{e.notes}</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {e.planned_hours > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-gray-500 font-mono">
                              <Clock className="w-3 h-3" />{e.planned_hours}h
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
